export const REPORT_LOCATION_STATUS = Object.freeze({
  IDLE: "idle",
  SEARCHING: "searching",
  AVAILABLE: "available",
  CONFIRMED: "confirmed",
  DECLINED: "declined",
  UNAVAILABLE: "unavailable",
  MAP_ERROR: "map-error",
});

export const REPORT_LOCATION_SOURCE = Object.freeze({
  PHOTO: "photo",
  BROWSER: "browser",
  ZIP: "zip",
  ARIZONA_FALLBACK: "arizona_fallback",
  USER_MOVED: "user_moved",
});

export function createReportLocationState() {
  return {
    status: REPORT_LOCATION_STATUS.IDLE,
    sourceType: "",
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip: "",
    extractedCoordinates: null,
    browserCoordinates: null,
    markerCoordinates: null,
    confirmedCoordinates: null,
    generation: 0,
    mapError: "",
  };
}

export function isValidCoordinatePair(coordinates) {
  return (
    coordinates &&
    typeof coordinates.latitude === "number" &&
    typeof coordinates.longitude === "number" &&
    Number.isFinite(coordinates.latitude) &&
    Number.isFinite(coordinates.longitude) &&
    coordinates.latitude >= -90 &&
    coordinates.latitude <= 90 &&
    coordinates.longitude >= -180 &&
    coordinates.longitude <= 180
  );
}

export function normalizedCoordinates(coordinates) {
  if (!isValidCoordinatePair(coordinates)) return null;
  return {
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  };
}

export function orderedReportPhotoSources({ animalPhotos = [], environmentPhotos = [] } = {}, keyForPhoto) {
  return [
    ...animalPhotos.map((file, index) => ({
      category: "animals",
      file,
      index,
      key: keyForPhoto(file, index, "animals"),
    })),
    ...environmentPhotos.map((file, index) => ({
      category: "environment",
      file,
      index,
      key: keyForPhoto(file, index, "environment"),
    })),
  ];
}

export function canAutoDiscoverReportLocation(state) {
  return [
    REPORT_LOCATION_STATUS.IDLE,
    REPORT_LOCATION_STATUS.SEARCHING,
    REPORT_LOCATION_STATUS.UNAVAILABLE,
    REPORT_LOCATION_STATUS.MAP_ERROR,
  ].includes(state.status) || (
    state.status === REPORT_LOCATION_STATUS.AVAILABLE &&
    state.sourceType !== REPORT_LOCATION_SOURCE.PHOTO
  );
}

export function shouldIncludeReportLocationStepForState({
  hasReportLocationCategory,
}) {
  if (!hasReportLocationCategory) return false;
  return true;
}

export function isConfirmedPhotoReportLocation(state) {
  return state.status === REPORT_LOCATION_STATUS.CONFIRMED && Boolean(state.sourcePhotoKey);
}

export function beginReportLocationSearch(state, sources) {
  if (!sources.length) return createReportLocationState();
  if (!canAutoDiscoverReportLocation(state)) return state;
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.SEARCHING,
    sourceType: "",
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip: "",
    extractedCoordinates: null,
    markerCoordinates: null,
    confirmedCoordinates: null,
    mapError: "",
    generation: state.generation + 1,
  };
}

export function selectFirstUsableReportLocation(sources, resultForKey) {
  for (const source of sources) {
    const result = resultForKey(source.key);
    if (!result || result.status === "pending") return { status: "waiting" };
    if (result.status === "found" && isValidCoordinatePair(result.coordinates)) {
      return {
        status: "found",
        source,
        coordinates: normalizedCoordinates(result.coordinates),
      };
    }
  }
  return { status: "unavailable" };
}

export function applyReportLocationSearchProgress(state, { generation, sources, resultForKey }) {
  if (generation !== state.generation) return state;
  if (state.status !== REPORT_LOCATION_STATUS.SEARCHING) return state;

  const selection = selectFirstUsableReportLocation(sources, resultForKey);
  if (selection.status === "waiting") return state;
  if (selection.status === "unavailable") {
    return {
      ...state,
      status: REPORT_LOCATION_STATUS.UNAVAILABLE,
      sourcePhotoKey: "",
      sourceCategory: "",
      extractedCoordinates: null,
      markerCoordinates: null,
      confirmedCoordinates: null,
      mapError: "",
    };
  }

  return {
    ...state,
    status: REPORT_LOCATION_STATUS.AVAILABLE,
    sourceType: REPORT_LOCATION_SOURCE.PHOTO,
    sourcePhotoKey: selection.source.key,
    sourceCategory: selection.source.category,
    sourceZip: "",
    extractedCoordinates: selection.coordinates,
    markerCoordinates: selection.coordinates,
    confirmedCoordinates: null,
    mapError: "",
  };
}

export function confirmReportLocation(state) {
  const coordinates = normalizedCoordinates(state.markerCoordinates);
  if (!coordinates) return state;
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.CONFIRMED,
    markerCoordinates: coordinates,
    confirmedCoordinates: coordinates,
    mapError: "",
  };
}

export function moveReportLocationMarker(state, coordinates) {
  const nextCoordinates = normalizedCoordinates(coordinates);
  if (!nextCoordinates) return state;
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.AVAILABLE,
    sourceType: REPORT_LOCATION_SOURCE.USER_MOVED,
    markerCoordinates: nextCoordinates,
    confirmedCoordinates: null,
    mapError: "",
  };
}

export function declineReportLocation(state) {
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.DECLINED,
    sourceType: "",
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip: "",
    extractedCoordinates: null,
    browserCoordinates: null,
    markerCoordinates: null,
    confirmedCoordinates: null,
    mapError: "",
  };
}

export function reportLocationMapFailed(state, message = "") {
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.MAP_ERROR,
    confirmedCoordinates: null,
    mapError: message,
  };
}

export function reportLocationDecisionResolved(state) {
  return state.status === REPORT_LOCATION_STATUS.CONFIRMED;
}

export function confirmedReportCoordinates(state) {
  return normalizedCoordinates(state.confirmedCoordinates);
}

export function reportLocationSourceRemoved(state, sourcePhotoKey) {
  if (!sourcePhotoKey || state.sourcePhotoKey !== sourcePhotoKey) return state;
  if ([
    REPORT_LOCATION_STATUS.AVAILABLE,
    REPORT_LOCATION_STATUS.CONFIRMED,
    REPORT_LOCATION_STATUS.SEARCHING,
  ].includes(state.status)) {
    return {
      ...createReportLocationState(),
      generation: state.generation,
    };
  }
  return state;
}

export function setReportLocationBrowserCoordinates(state, coordinates) {
  const nextCoordinates = normalizedCoordinates(coordinates);
  if (!nextCoordinates) return state;
  if (
    [
      REPORT_LOCATION_STATUS.CONFIRMED,
      REPORT_LOCATION_STATUS.DECLINED,
      REPORT_LOCATION_STATUS.MAP_ERROR,
    ].includes(state.status) ||
    [REPORT_LOCATION_SOURCE.PHOTO, REPORT_LOCATION_SOURCE.USER_MOVED].includes(state.sourceType)
  ) {
    return state;
  }
  return {
    ...state,
    browserCoordinates: nextCoordinates,
    status: REPORT_LOCATION_STATUS.AVAILABLE,
    sourceType: REPORT_LOCATION_SOURCE.BROWSER,
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip: "",
    extractedCoordinates: nextCoordinates,
    markerCoordinates: nextCoordinates,
    confirmedCoordinates: null,
    mapError: "",
  };
}

export function initializeReportLocationPin(state, coordinates, sourceType, { sourceZip = "" } = {}) {
  const nextCoordinates = normalizedCoordinates(coordinates);
  if (!nextCoordinates) return state;
  return {
    ...state,
    status: REPORT_LOCATION_STATUS.AVAILABLE,
    sourceType,
    sourcePhotoKey: "",
    sourceCategory: "",
    sourceZip,
    extractedCoordinates: nextCoordinates,
    markerCoordinates: nextCoordinates,
    confirmedCoordinates: null,
    mapError: "",
  };
}

export function reportLocationZipChanged(state) {
  return {
    ...createReportLocationState(),
    generation: state.generation + 1,
  };
}
